// Optimized responsive image renditions (AVIF + WebP, 1x + 2x) hosted on the
// project CDN. Each entry also keeps the original raster as a last-resort
// <img> fallback for legacy browsers (never fetched by modern browsers, which
// take the matching <source>). Combined payload for these nine images is
// ~549 KiB of AVIF (down from ~11,029 KiB of oversized originals).

const BASE = "https://vibe.filesafe.space/1790273207865208150/assets";

export type Rendition = {
  avif1: string;
  avif2: string;
  webp1: string;
  webp2: string;
  fallback: string;
  w: number;
  h: number;
  // When set, no optimized AVIF/WebP renditions exist yet — render a plain
  // <img> pointing at this original asset URL instead of a broken <picture>.
  plainSrc?: string;
};

const r = (
  a1: string,
  w1: string,
  a2: string,
  w2: string,
  w: number,
  h: number,
  _fb?: string,
): Rendition => ({
  avif1: `${BASE}/${a1}.avif`,
  avif2: `${BASE}/${a2}.avif`,
  webp1: `${BASE}/${w1}.webp`,
  webp2: `${BASE}/${w2}.webp`,
  w,
  h,
  // Fallback to the optimized 1x WebP rendition so browsers without AVIF
  // or tools inspecting the <img> src never download the 4MB-11MB original raster.
  fallback: `${BASE}/${w1}.webp`,
});

// Rendition for an asset that has NO optimized AVIF/WebP versions yet.
// ResponsiveImage renders a plain <img> from `plainSrc` (the original
// attachment URL) so the image still displays instead of 404ing.
const plain = (src: string, w: number, h: number): Rendition => ({
  avif1: "",
  avif2: "",
  webp1: "",
  webp2: "",
  fallback: src,
  plainSrc: src,
  w,
  h,
});

const ATTACH = "https://vibe.filesafe.space/1785525013889768799/attachments";

export const IMG = {
  // 1 — Triple ISO-Certified Craftsmanship (main card, 4:3 / aspect-video)
  iso: r(
    "8ff64b24-e98e-498c-a237-4077d2bb98dd",
    "06de95af-7e69-4625-879c-7b6e9b44d619",
    "c19c8610-fe18-469d-a194-bff211c7df1c",
    "c0d61f82-ca72-4a1e-853f-02b5d3862d33",
    634,
    475,
  ),
  // 2 — Japandi reception counter (IG thumb)
  japandi: r(
    "060d1df9-d34c-4f06-81b8-96ab863e1566",
    "58ca6d5a-8d4f-4d7f-a9c8-d64ea4f0d099",
    "ae014315-8024-4506-ae1b-53d81654646d",
    "cfda8525-b69f-4733-a7ca-2b726a64acff",
    266,
    333,
  ),
  // 3 — Marble in the CEO's Office (IG thumb)
  marble: r(
    "b626ee90-9ec2-49e7-ae90-486c09df421d",
    "f581bd18-d857-41de-b87a-9d1f75ab4698",
    "ee86e59b-8d8b-4c6f-ad79-8da4ffe693ed",
    "322dc678-b20c-49d4-bda0-e17ae19e9198",
    266,
    335,
  ),
  // 4 — SAVANNA Flagship Store (IG thumb)
  savanna: r(
    "ceb99349-ff7a-457a-ad31-2b04d87f9328",
    "05c41650-9af5-4020-8412-1c094a41c29c",
    "45aa8bb1-224f-4e8d-94bd-3b9e5588e7d5",
    "2cd3742a-f0e3-4f14-8567-d712259c7b93",
    266,
    335,
  ),
  // 5 — Your Brand Is Our Reputation (main card, wide)
  brand: r(
    "ae65276c-9c55-4ad4-864d-bd31d085097c",
    "24cc1ea4-92bd-414a-be28-ddf12f8800c5",
    "9feefd5f-6be3-45bb-9e2c-ae2875bebf0a",
    "e1dc4b93-c607-4659-aa81-d3e3e694c470",
    704,
    475,
  ),
  // 6 — Zero Missed Deadlines in 24 Years (main card, 4:3)
  deadlines: r(
    "ac1d36fd-6a76-47ad-b351-eac2e99e45a0",
    "23a5ba0a-b0d1-44b6-a54b-0133b25d3f99",
    "730210ac-b99f-44c9-8c1a-4a63ed19c594",
    "eceaf4eb-f74a-4922-b68f-8915f8f95164",
    634,
    475,
  ),
  // 7 — Hackett London store reveal (IG thumb)
  hackett: r(
    "74fdea08-782a-4ec1-8296-9af8a28efc97",
    "a542cc0f-02b5-4c15-9e26-5964251e41dd",
    "c81a4a02-8d27-416b-b426-9b03efc34dce",
    "c7415e59-47ab-4b8d-8c0e-4bcea1419fa1",
    266,
    469,
  ),
  // 8 — Hero one-stop-solution logo (LCP element, eager/high-priority)
  heroLogo: r(
    "351c9d56-1e7c-4d72-be0d-493bd923d5b5",
    "e6c4e45b-88a9-4beb-a022-44525fe7ff13",
    "c942dc9d-0f67-4954-af05-72db457cdeef",
    "f27d3d64-1117-406a-8140-be9306f2905c",
    234,
    165,
  ),
  // 9 — Abacus Design Solutions wordmark (navbar/footer logo)
  logo: r(
    "78340001-38a9-4940-8983-4d104fc05e89",
    "e7d7381d-032a-4970-b55d-c5ee362f3d2a",
    "167197a9-5d6d-4e9e-a7be-2c9f6fd807f5",
    "7f1ef2c8-88b6-449b-9b17-5f7789673219",
    167,
    70,
  ),
  // 10 — Featured project (illy Café), 800x500 card
  illyFeatured: plain(
    "https://vibe.filesafe.space/1790273207865208150/attachments/419f1f52-9f5f-4a69-8927-8dfd6ff7047d.webp",
    800,
    500,
  ),
  // 11 — MaxMara project grid card, 400x256
  maxmara: plain(
    "https://vibe.filesafe.space/1790273207865208150/attachments/33d62c95-fa1c-4309-9b24-41af40d3d68b.webp",
    400,
    256,
  ),
  // 12 — Malabar Gold project grid card, 400x256
  malabar: plain(
    "https://vibe.filesafe.space/1790273207865208150/attachments/08bfe76d-c1d1-4488-add2-50c88db33a6e.webp",
    400,
    256,
  ),
  // 13 — Al Salam Bank project grid card, 400x256
  alsalam: plain(
    "https://vibe.filesafe.space/1790273207865208150/attachments/b4171eb5-e0bd-4291-921e-5508a0fae2e8.webp",
    400,
    256,
  ),
} as const;
